import classNames from 'classnames';
import { add } from 'date-fns/add';
import { isBefore } from 'date-fns/isBefore';
import { sub } from 'date-fns/sub';
import { singularize, titleize } from 'inflection';
import useStyles from 'isomorphic-style-loader/useStyles';
import { type ReactElement, type KeyboardEvent, useState, useRef } from 'react';
import ReactDatePicker, {
  type DatePickerProps as ReactDatePickerProps,
} from 'react-datepicker';
import {
  Button,
  type ButtonProps,
  Divider,
  Form,
  Icon,
} from 'semantic-ui-react';
import type { ArrayValues } from 'type-fest';

import s from './date-picker.scss';

export type DatePickerProps = Omit<
  ReactDatePickerProps,
  'onChange' | 'selected'
> & {
  onChange: (value: Date | null) => void;
  value: string | boolean;
  disabled?: boolean;
  customInput?: ReactElement;
  className?: string;
  buttonProps?: ButtonProps;
  setCalendarOpen?: (open: boolean) => void;
  showTime?: boolean;
  preventOpenOnFocus?: boolean;
};

const dateOffsets = ['minutes', 'hours', 'days', 'weeks', 'months'] as const;
type DateOffsetUnit = ArrayValues<typeof dateOffsets>;

const DatePicker = ({
  onChange,
  value = '',
  customInput,
  disabled = false,
  className,
  buttonProps,
  setCalendarOpen,
  showTime = true,
  preventOpenOnFocus = false,
}: DatePickerProps) => {
  useStyles(s);

  // Hooks to hold the date range and count values for offset dates
  const [dateOffsetCount, setDateOffsetCount] = useState(1);
  const [dateOffsetRange, setDateOffsetRange] =
    useState<DateOffsetUnit>('weeks');
  const [dateOffsetDirection, setDateOffsetDirection] = useState(
    value && isBefore(new Date(value), new Date()) ? 'before' : 'from',
  );

  const displayedDate = value ? new Date(value) : null;
  const formattedTime = showTime
    ? displayedDate?.toString()
    : displayedDate?.toDateString();

  // Reference to the calendar control internal API
  const calendarApi = useRef<ReactDatePicker>(null);

  return (
    <ReactDatePicker
      ref={calendarApi}
      className={className}
      customInput={
        customInput || (
          <div className={s.root}>
            <Button icon labelPosition="right" basic fluid {...buttonProps}>
              <Icon name="calendar alternate outline" />
              <input
                className={classNames(s.buttonInput, 'darkInput')}
                value={formattedTime || 'N/A'}
                onChange={() => undefined}
              />
            </Button>
          </div>
        )
      }
      onCalendarOpen={() => setCalendarOpen?.(true)}
      onCalendarClose={() => setCalendarOpen?.(false)}
      shouldCloseOnSelect={false}
      placeholderText="Enter a Value"
      disabled={disabled}
      openToDate={displayedDate ?? undefined}
      dateFormat="yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
      timeFormat="HH:mm"
      withPortal
      portalId="portalRoot"
      preventOpenOnFocus={preventOpenOnFocus}
    >
      <div className={s.datePickerContentsWrapper}>
        <Button
          className={s.setDateBtn}
          color="blue"
          fluid
          onClick={() => {
            if (calendarApi.current) {
              onChange(calendarApi.current.state.preSelection ?? null);
              calendarApi.current.setOpen(false);
            }
          }}
        >
          Set Date
        </Button>
        <div className={s.offsetPickerWrapper}>
          <div className={s.fillerBlock} />
          <div>
            <Divider className={s.divider} vertical>
              Or
            </Divider>
          </div>
          <div className={s.formWrapper}>
            <Form>
              <Form.Input
                type="number"
                autoFocus
                value={dateOffsetCount || ''}
                onKeyDown={(ev: KeyboardEvent<HTMLInputElement>) => {
                  if (ev.code === 'Enter' && dateOffsetCount) {
                    onChange(
                      add(new Date(), { [dateOffsetRange]: dateOffsetCount }),
                    );
                    calendarApi.current?.setOpen(false);
                  }
                }}
                onChange={(ev, data) => {
                  const dateOffsetCountInt = parseInt(data.value.trim(), 10);
                  if (
                    typeof dateOffsetCountInt === 'number' &&
                    dateOffsetCountInt > 0
                  ) {
                    setDateOffsetCount(dateOffsetCountInt);
                  } else if (!dateOffsetCountInt && dateOffsetCountInt !== 0) {
                    setDateOffsetCount(0);
                  }
                }}
              />
              <Form.Dropdown
                fluid
                selection
                options={dateOffsets.map(offset => ({
                  key: offset,
                  text:
                    dateOffsetCount === 1
                      ? singularize(titleize(offset))
                      : titleize(offset),
                  value: offset,
                }))}
                value={dateOffsetRange}
                onChange={(ev, data) => {
                  setDateOffsetRange(data.value as DateOffsetUnit);
                }}
              />
              <Form.Dropdown
                fluid
                selection
                value={dateOffsetDirection}
                onChange={(ev, data) => {
                  setDateOffsetDirection(data.value as string);
                }}
                options={[
                  { key: 'before', text: 'Before Now', value: 'before' },
                  { key: 'from', text: 'From Now', value: 'from' },
                ]}
              />
            </Form>
            <Button
              color="blue"
              disabled={!dateOffsetCount}
              onClick={() => {
                const duration = { [dateOffsetRange]: dateOffsetCount };
                onChange(
                  dateOffsetDirection === 'from'
                    ? add(new Date(), duration)
                    : sub(new Date(), duration),
                );
                calendarApi.current?.setOpen(false);
              }}
            >
              Apply
            </Button>
            <div className={classNames(s.sadRobot, 'darkSVG')} />
          </div>
        </div>
      </div>
    </ReactDatePicker>
  );
};

export default DatePicker;
